import hoistStatics from 'hoist-non-react-statics'
import { Waypoint } from 'react-waypoint'
import PropTypes from 'prop-types'
import getDisplayName from '@Helpers/getDisplayName'
import document from 'global/document'
import window from 'global/window'
import React, { Component } from 'react'
import { injectIntl } from 'react-intl'
import classNames from 'classnames'
import { action, autorun, computed, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import addClass from 'add-class'
import styled from 'styled-components'
import getNodeDimensions from './getNodeDimensions'
import getNodeBackgroundImage from './getNodeBackgroundImage'
import getDimensionsWithinBounds from './getDimensionsWithinBounds'
import toCloudinaryUrl from './toCloudinaryUrl'
import messages from '../messages'

function injectCloudinaryHost(Source) {
  class Wrapped extends Component {
    static displayName = getDisplayName(Source)
    static contextTypes = {
      cloudinaryURL: PropTypes.string,
    }

    render() {
      return (
        <Source
          {...this.props}
          cloudinaryURL={
            this.context.cloudinaryURL || process.env.NODE_ENV == 'production'
              ? 'https://assets.visioncritical.net/image'
              : 'https://qa-assets.visioncritical.net/image'
          }
        />
      )
    }
  }

  return hoistStatics(Wrapped, Source)
}

@observer
class ImageContainer extends Component {
  @observable actualUrl = null
  @observable isVisible = false
  @observable retries = 0

  _image = null
  _imageInner = null
  _unmounted = false
  _dispose = null

  constructor(props) {
    super(props)

    if (props.ssr) {
      this.updateUrl(props.url)
    }
  }

  @computed
  get maxRetries() {
    return typeof this.props.maxRetries === 'number' ? this.props.maxRetries : 5
  }

  componentDidMount() {
    const f = autorun(() => this.processPropChanges())
    const g = autorun(() => this.retryUntilActualUrlUpdates())
    const h = autorun(() => this.processImageUpdate())
    this._dispose = () => {
      f()
      g()
      h()
    }

    if (this.props.delay !== true) {
      this.setVisibility(true)
    }
  }

  componentWillUnmount() {
    const { _dispose } = this
    _dispose && _dispose()
  }

  @action
  setVisibility = (visible) => {
    this.isVisible = visible
  }

  @action
  onEnter = () => {
    this.setVisibility(true)
  }

  @action
  updateUrl(url) {
    this.actualUrl = url
  }

  // Tries to build the actual image URL from the different parameters.
  processPropChanges() {
    const { actualUrl, retries } = this
    const { url, op, resolutionScale, quality, crop, box, radius, angle, effect, opacity, format } =
      this.props

    if (retries < this.maxRetries) {
      return
    }

    if (typeof url !== 'string') {
      this.updateUrl(null)
      return
    }

    const dimensions = getNodeDimensions(this._image)

    if (dimensions === null) {
      return
    }

    const backgroundImage = getNodeBackgroundImage(this._imageInner)

    // The backgroundImage may have been modified by an addon, in which case we leave it up to the owner to handle it.
    if (
      actualUrl !== null &&
      backgroundImage !== null &&
      backgroundImage.indexOf(actualUrl) === -1
    ) {
      return
    }

    const { width, height } = getDimensionsWithinBounds(
      dimensions.width,
      dimensions.height,
      resolutionScale
    )

    this.updateUrl(
      toCloudinaryUrl(
        this.props.cloudinaryURL,
        typeof url === 'string' ? url : null,
        width,
        height,
        op,
        box,
        crop,
        radius,
        angle,
        effect,
        quality,
        opacity,
        format
      )
    )
  }

  retryUntilActualUrlUpdates = () => {
    const { retries, actualUrl } = this
    if (actualUrl === null || this._unmounted) {
      window.requestAnimationFrame(() => {
        runInAction(() => {
          if (this.retries < this.maxRetries) {
            this.retries = retries + 1
          }
        })
      })
    }
  }

  processImageUpdate = () => {
    const { actualUrl, isVisible } = this

    if (!isVisible) {
      return
    }

    if (actualUrl === null) {
      this.updateUrl(null)
    } else {
      this.resolveUrl(actualUrl)
    }
  }

  resolveUrl(url) {
    const resolver = this.props.resolver || this.defaultResolver
    const node = this._image

    resolver(url)
      .then(() => {
        addClass(node, 'px-image-ready')
        if (this.props.onLoad) {
          this.props.onLoad()
        }
      })
      .catch(() => {
        addClass(node, 'px-image-failed')
        if (this.props.onLoadError) {
          this.props.onLoadError()
        }
      })
  }

  defaultResolver = (url) => {
    return new Promise((res, rej) => {
      const node = this._image
      if (node) {
        const img = document.createElement('img')
        img.src = url

        img.onload = () => {
          delete img.onload
          delete img.onerror
          res()
        }

        img.onerror = () => {
          delete img.onload
          delete img.onerror
          rej()
        }
      }
    })
  }

  render() {
    const {
      delay,
      className,
      imageClassName,
      url,
      op,
      style,
      label,
      intl,
      ssr,
      avatar,
      width,
      height,
    } = this.props
    const ready = ssr ? 'px-image-ready' : ''

    const inlineStyles = {
      backgroundImage:
        typeof this.actualUrl === 'string' ? `url('${this.actualUrl}')` : `url('${url}')`,
      backgroundSize: op === 'contain' ? 'contain' : 'cover',
    }

    return (
      <Container
        avatar={avatar}
        width={width}
        height={height}
        ref={(r) => (this._image = r)}
        className={classNames(`px-image-container imageContainer ${ready}`, className)}
        style={{ ...style }}
      >
        {delay === true ? <Waypoint onEnter={this.onEnter} /> : null}
        <Image
          role="img"
          aria-label={label || intl.formatMessage(messages.unlabeledImageText)}
          alt={label || intl.formatMessage(messages.unlabeledImageText)}
          ref={(r) => (this._imageInner = r)}
          className={classNames('px-image', 'image', imageClassName)}
          style={inlineStyles}
          data-original-url={url}
        />
        {this.props.children}
      </Container>
    )
  }
}

const Container = styled.div`
  position: relative;
  overflow: hidden;
  background-color: transparent;

  > span {
    position: absolute;
    top: 0;
  }

  ${(props) =>
    props.avatar &&
    `
    width: ${props.width}px;
    height: ${props.height}px;
  `}
`

const Image = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: 50% 50%;
`

export { toCloudinaryUrl, injectCloudinaryHost }
export default injectCloudinaryHost(injectIntl(ImageContainer))
